import '@oysterjs/corev2/apm';
import * as Sentry from '@sentry/react';

import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './api/client';
import { DashboardContainer } from './components/dashboard';
import { HomePage } from './pages/home';
import {
  PoliciesPage,
  PolicyCreatePage,
  PolicyDocumentsPage,
  PolicyDocumentUploadPage,
  PolicyEditPage,
  PolicyExplorerPage,
  PolicyHistoryPage,
  PolicyOverviewPage,
  PolicyPage
} from './pages/knowledge/policies';
import {
  AccountCertificateCreatePage,
  AccountCertificatePage,
  AccountCertificatesPage,
  AccountCreatePage,
  AccountEditPage,
  AccountOverviewPage,
  AccountPage,
  AccountPoliciesPage,
  AccountServiceRequestsPage,
  AccountsPage
} from './pages/knowledge/accounts';
import { SignInPage } from './pages/signin';
import {
  ServiceRequestCommentsAndActivityPage,
  ServiceRequestConversationPage,
  ServiceRequestOverviewPage,
  ServiceRequestPage,
  ServicingPage
} from './pages/servicing';
import * as actions from './api/actions';
import * as loaders from './api/loaders';
import { byMethod } from '@oysterjs/corev2/graphql/actions';
import { merge } from '@oysterjs/corev2/graphql/loaders';
import { ErrorBoundary } from '@oysterjs/uiv2/error-boundary';
import { LandingPage } from './pages/landing';
import { CallPage, CallsPage } from './pages/calls';
import { EmailThreadsPage } from './pages/emails';
import { EmailThreadPage } from './pages/emails';
import {
  AgencyUserPreferencesPage,
  CopilotWorkspacePage,
  CopilotWorkspacesPage
} from './pages/copilot/copilot';
import {
  PresetCreatePage,
  PresetEditPage,
  PresetRunPage,
  PresetsPage
} from './pages/copilot/presets';
import {
  DocumentTemplateCreatePage,
  DocumentTemplateEditPage,
  DocumentTemplatesPage
} from './pages/copilot/styles';
import {
  KnowledgeBaseDocumentCreatePage,
  KnowledgeBaseDocumentEditPage,
  KnowledgeBaseDocumentPage,
  KnowledgeBasePage
} from './pages/knowledge/knowledge';

const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)([
  {
    id: 'app',
    path: 'app',
    loader: merge({ agencyUser: loaders.getAgencyUser }),
    element: <DashboardContainer />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '',
        element: <Navigate to="home" replace />
      },
      {
        path: 'home',
        loader: merge({ serviceRequests: loaders.getServiceRequests }),
        element: <HomePage />,
        children: [
          {
            path: 'preferences',
            action: byMethod({ PUT: actions.updateAgencyUserPreferences }),
            loader: merge({ agencyUser: loaders.getAgencyUser, presets: loaders.getCustomPresets }),
            element: <AgencyUserPreferencesPage />
          },
          {
            path: 'presets/:id',
            loader: merge({ preset: loaders.getCustomPreset }),
            element: <PresetRunPage />
          }
        ]
      },
      {
        path: 'workspaces',
        loader: merge({ caraWorkspaces: loaders.getCaraWorkspaces }),
        element: <CopilotWorkspacesPage />,
        children: [
          {
            path: 'archive',
            action: byMethod({ POST: actions.archiveCaraWorkspaces })
          }
        ]
      },
      {
        path: 'workspaces/:id',
        loader: merge({
          caraWorkspace: loaders.getCaraWorkspace,
          htmlDocumentTemplates: loaders.getHtmlDocumentTemplates
        }),
        element: <CopilotWorkspacePage />
      },
      {
        path: 'presets',
        loader: merge({ presets: loaders.getCustomPresets }),
        element: <PresetsPage />,
        children: [
          {
            path: 'new',
            action: byMethod({ POST: actions.createCustomPreset }),
            element: <PresetCreatePage />
          },
          {
            path: ':id/edit',
            loader: merge({ preset: loaders.getCustomPreset }),
            action: byMethod({ PUT: actions.updateCustomPreset }),
            element: <PresetEditPage />
          },
          {
            path: ':id/run',
            loader: merge({ preset: loaders.getCustomPreset }),
            element: <PresetRunPage />
          }
        ]
      },
      {
        path: 'styles',
        loader: merge({ htmlDocumentTemplates: loaders.getHtmlDocumentTemplates }),
        element: <DocumentTemplatesPage />,
        children: [
          {
            path: 'new',
            action: byMethod({ POST: actions.createHtmlDocumentTemplate }),
            element: <DocumentTemplateCreatePage />
          },
          {
            path: ':id',
            loader: merge({ htmlDocumentTemplate: loaders.getHtmlDocumentTemplate }),
            action: byMethod({ PUT: actions.updateHtmlDocumentTemplate }),
            element: <DocumentTemplateEditPage />
          }
        ]
      },
      {
        path: 'calls',
        loader: merge({ calls: loaders.getCalls }),
        element: <CallsPage />,
        children: [
          {
            path: ':id',
            loader: merge({ call: loaders.getCall }),
            element: <CallPage />
          }
        ]
      },
      {
        path: 'emails',
        loader: merge({ emailThreads: loaders.getEmailThreads }),
        element: <EmailThreadsPage />,
        children: [
          {
            path: ':id',
            loader: merge({ emailThread: loaders.getEmailThread }),
            element: <EmailThreadPage />
          }
        ]
      },
      {
        path: 'accounts',
        loader: merge({ accounts: loaders.getAccounts }),
        element: <AccountsPage />,
        children: [
          {
            path: 'new',
            action: byMethod({ POST: actions.createAccount }),
            element: <AccountCreatePage />
          }
        ]
      },
      {
        id: 'accounts/:id',
        path: 'accounts/:id',
        loader: merge({ account: loaders.getAccount }),
        element: <AccountPage />,
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />
          },
          {
            path: 'edit',
            loader: merge({ account: loaders.getAccount }),
            action: byMethod({ PUT: actions.updateAccount }),
            element: <AccountEditPage />
          },
          {
            path: 'overview',
            element: <AccountOverviewPage />
          },
          {
            path: 'policies',
            element: <AccountPoliciesPage />
          },
          {
            path: 'certificates',
            element: <AccountCertificatesPage />,
            children: [
              {
                path: 'new',
                action: byMethod({ POST: actions.createCertificateOfLiabilityInsurance }),
                element: <AccountCertificateCreatePage />
              },
              {
                id: 'accounts/:id/certificates/:certificateId',
                path: ':certificateId',
                loader: merge({ certificate: loaders.getCertificate }),
                element: <AccountCertificatePage />
              }
            ]
          },
          {
            path: 'service',
            element: <AccountServiceRequestsPage />
          }
        ]
      },
      {
        path: 'policies',
        loader: merge({ businessPolicies: loaders.getBusinessPolicies }),
        element: <PoliciesPage />,
        children: [
          {
            id: 'policies/new',
            path: 'new',
            loader: merge({
              accounts: loaders.getAccounts,
              agencyUsers: loaders.getAgencyUsers,
              carriers: loaders.getInsuranceCarriers
            }),
            shouldRevalidate: () => false,
            action: byMethod({ POST: actions.createBusinessPolicy }),
            element: <PolicyCreatePage />
          }
        ]
      },
      {
        id: 'policies/:id',
        path: 'policies/:id',
        loader: merge({ businessPolicy: loaders.getBusinessPolicy }),
        element: <PolicyPage />,
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />
          },
          {
            path: 'edit',
            loader: merge({
              businessPolicy: loaders.getBusinessPolicy,
              agencyUsers: loaders.getAgencyUsers,
              carriers: loaders.getInsuranceCarriers
            }),
            action: byMethod({ PUT: actions.updateBusinessPolicy }),
            element: <PolicyEditPage />
          },
          {
            path: 'overview',
            element: <PolicyOverviewPage />
          },
          {
            path: 'documents',
            element: <PolicyDocumentsPage />,
            action: byMethod({ DELETE: actions.deleteFile }),
            children: [
              {
                path: 'new',
                action: byMethod({ POST: actions.uploadPolicyFile }),
                element: <PolicyDocumentUploadPage />
              }
            ]
          },
          {
            path: 'history',
            element: <PolicyHistoryPage />
          },
          {
            path: 'explore',
            loader: merge({
              policyExplorerConversation: loaders.getPolicyExplorerConversation
            }),
            shouldRevalidate: () => false,
            action: byMethod({ POST: actions.addPolicyExplorerConversationMessage }),
            element: <PolicyExplorerPage />
          }
        ]
      },
      {
        path: 'knowledge',
        loader: merge({
          knowledgeBase: loaders.getDefaultKnowledgeBase
        }),
        action: byMethod({ POST: actions.uploadKnowledgeBaseFileDocument }),
        element: <KnowledgeBasePage />,
        children: [
          {
            path: 'new',
            action: byMethod({ POST: actions.createKnowledgeBaseTextDocument }),
            element: <KnowledgeBaseDocumentCreatePage />
          },
          {
            path: ':id',
            loader: merge({ knowledgeBaseDocument: loaders.getKnowledgeBaseDocument }),
            action: byMethod({ DELETE: actions.deleteKnowledgeBaseDocument }),
            element: <KnowledgeBaseDocumentPage />
          },
          {
            path: ':id/edit',
            loader: merge({ knowledgeBaseDocument: loaders.getKnowledgeBaseDocument }),
            action: byMethod({ PUT: actions.updateKnowledgeBaseDocument }),
            element: <KnowledgeBaseDocumentEditPage />
          }
        ]
      },
      {
        path: 'servicing',
        loader: merge({ serviceRequests: loaders.getServiceRequests }),
        element: <ServicingPage />
      },
      {
        id: 'servicing/:id',
        path: 'servicing/:id',
        loader: merge({
          serviceRequest: loaders.getServiceRequest,
          agencyUsers: loaders.getAgencyUsers
        }),
        element: <ServiceRequestPage />,
        children: [
          {
            index: true,
            element: <ServiceRequestOverviewPage />
          },
          {
            path: 'resolve',
            action: byMethod({ POST: actions.resolveServiceRequest }),
            element: <Navigate to="../overview" replace />
          },
          {
            path: 'update',
            action: byMethod({ POST: actions.updateServiceRequest }),
            element: <Navigate to="../overview" replace />
          },
          {
            path: 'overview',
            element: <ServiceRequestOverviewPage />
          },
          {
            path: 'conversation',
            element: <ServiceRequestConversationPage />
          },
          {
            path: 'activity',
            element: <ServiceRequestCommentsAndActivityPage />,
            children: [
              {
                path: 'comment',
                action: byMethod({ POST: actions.addServiceRequestComment }),
                element: <Navigate to="../activity" replace />
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: 'app/signin',
    element: <SignInPage />
  },
  {
    path: 'signin',
    element: <Navigate to="/app/signin" replace />
  },
  {
    path: '',
    element: <LandingPage />
  }
]);

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container!);

root.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <RouterProvider router={router} />
    </ApolloProvider>
  </React.StrictMode>
);
